// Home, privacy, licences and 404 pages.
import { OFFICE_ENABLED, SOURCE_URL, netserenoLink, resourcesLink } from '../config';
import { t } from '../i18n';
import { CATEGORIES, type Lang, type ToolMeta, enabledTools, homePath, pagePath, toolPath } from '../registry';
import { h } from './dom';
import { icon } from './icons';

export function toolCard(tool: ToolMeta, lang: Lang): HTMLElement {
  const d = t();
  return h(
    'a',
    { class: `tool-card cat-${tool.category}`, href: toolPath(lang, tool.id), dataset: { tool: tool.id } },
    h('span', { class: 'tool-icon' }, icon(tool.icon)),
    h('span', null, h('h3', null, d.tools[tool.id].name), h('p', null, d.tools[tool.id].description)),
  );
}

/** Lowercase, accent-free text for search. */
const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export function homePage(lang: Lang): HTMLElement {
  const d = t();
  const tools = enabledTools(OFFICE_ENABLED);

  const categories = CATEGORIES.map((cat) => {
    const inCat = tools.filter((tool) => tool.category === cat);
    if (!inCat.length) return null;
    const cards = inCat.map((tool) => toolCard(tool, lang));
    return { cat, el: h('section', { class: 'category', 'aria-labelledby': `cat-${cat}` }, h('h3', { class: 'category-title', id: `cat-${cat}` }, d.categories[cat]), h('div', { class: 'tool-grid' }, cards)), cards, inCat };
  }).filter((c) => c !== null);

  const noResults = h('p', { class: 'hint', hidden: true }, d.home.noResults);
  const search = h('input', {
    type: 'search',
    id: 'tool-search',
    placeholder: d.home.searchPlaceholder,
    autocomplete: 'off',
    oninput: () => {
      const q = norm(search.value.trim());
      let any = false;
      for (const c of categories) {
        let visible = 0;
        c.inCat.forEach((tool, i) => {
          const text = norm(`${d.tools[tool.id].name} ${d.tools[tool.id].description} ${d.categories[tool.category]}`);
          const match = !q || q.split(/\s+/).every((w) => text.includes(w));
          c.cards[i].hidden = !match;
          if (match) visible++;
        });
        c.el.hidden = visible === 0;
        any ||= visible > 0;
      }
      noResults.hidden = any;
    },
  });

  const trustIcons = ['shield-check', 'wifi-off', 'zap', 'languages'];
  return h(
    'div',
    null,
    h(
      'section',
      { class: 'hero' },
      h(
        'div',
        { class: 'container' },
        h('div', { class: 'hero-badge' }, icon('shield-check'), d.home.badge),
        h('h1', null, d.home.heroTitle),
        h('p', { class: 'hero-sub' }, d.home.heroSubtitle),
        h(
          'div',
          { class: 'hero-actions' },
          h('a', { class: 'btn btn-primary btn-lg', href: '#tools' }, d.home.ctaPrimary, icon('arrow-down')),
          h('a', { class: 'btn btn-outline btn-lg', href: '#why' }, d.home.ctaSecondary),
        ),
        h(
          'div',
          { class: 'trust-row' },
          d.home.trust.map((item, i) => h('div', { class: 'trust-item' }, icon(trustIcons[i] ?? 'check'), h('div', null, h('strong', null, item.title), h('span', null, item.text)))),
        ),
      ),
    ),
    h(
      'section',
      { class: 'section', id: 'tools' },
      h(
        'div',
        { class: 'container' },
        h('span', { class: 'eyebrow' }, d.home.toolsEyebrow),
        h('h2', { class: 'section-title' }, d.nav.allTools),
        h('div', { class: 'tool-search', role: 'search' }, icon('search'), h('label', { class: 'visually-hidden', for: 'tool-search' }, d.home.searchLabel), search),
        noResults,
        categories.map((c) => c.el),
      ),
    ),
    h(
      'section',
      { class: 'section', id: 'how' },
      h('div', { class: 'container' }, h('h2', { class: 'section-title' }, d.home.howTitle), h('div', { class: 'steps' }, d.home.howSteps.map((s) => h('div', { class: 'step' }, h('h3', null, s.title), h('p', null, s.text))))),
    ),
    h(
      'section',
      { class: 'section', id: 'why' },
      h(
        'div',
        { class: 'container' },
        h(
          'div',
          { class: 'why' },
          h('div', { class: 'why-visual', 'aria-hidden': 'true' }, icon('shield-check')),
          h('div', null, h('h2', { class: 'section-title' }, d.home.whyTitle), h('p', null, d.home.whyText), h('a', { href: pagePath(lang, 'privacy') }, d.footer.privacy, ' →')),
        ),
      ),
    ),
    h(
      'section',
      { class: 'section faq', id: 'faq' },
      h('div', { class: 'container' }, h('h2', { class: 'section-title' }, d.home.faqTitle), d.home.faq.map((item) => h('details', null, h('summary', null, item.q), h('p', null, item.a)))),
    ),
    h(
      'section',
      { class: 'section' },
      h(
        'div',
        { class: 'container' },
        h(
          'div',
          { class: 'cta-band' },
          h('div', null, h('span', { class: 'eyebrow' }, d.home.ctaEyebrow), h('h2', null, d.home.ctaTitle), h('p', null, d.home.ctaText)),
          h(
            'div',
            { class: 'cta-actions' },
            h('a', { class: 'btn btn-accent btn-lg', href: netserenoLink('home-cta'), target: '_blank', rel: 'noopener' }, d.home.ctaButton, icon('external-link')),
            h('a', { class: 'btn btn-outline btn-lg', href: resourcesLink('home-cta'), target: '_blank', rel: 'noopener' }, d.home.ctaResources),
          ),
        ),
      ),
    ),
  );
}

export function privacyPage(lang: Lang): HTMLElement {
  const d = t();
  const p = d.pages.privacy;
  return h(
    'div',
    { class: 'container section' },
    h('div', { class: 'prose' }, h('h1', null, p.title), h('p', null, p.intro), p.sections.map((s) => [h('h2', null, s.title), h('p', null, s.text)]), h('p', null, h('a', { href: pagePath(lang, 'licenses') }, d.footer.licenses, ' →'))),
  );
}

const LICENSES: Array<{ name: string; url: string; license: string; usage: string }> = [
  { name: 'pdf-lib (Cantoo fork)', url: 'https://github.com/cantoo-scribe/pdf-lib', license: 'MIT', usage: 'PDF' },
  { name: 'PDF.js', url: 'https://github.com/mozilla/pdf.js', license: 'Apache-2.0', usage: 'Render / text' },
  { name: 'Tesseract.js / Tesseract OCR', url: 'https://github.com/naptha/tesseract.js', license: 'Apache-2.0', usage: 'OCR' },
  { name: 'tessdata (cat, spa, glg, eng)', url: 'https://github.com/tesseract-ocr/tessdata_best', license: 'Apache-2.0', usage: 'OCR' },
  { name: 'qpdf (WebAssembly)', url: 'https://github.com/qpdf/qpdf', license: 'Apache-2.0', usage: 'Repair / optimise' },
  { name: 'Ghostscript (WebAssembly)', url: 'https://github.com/okathira/ghostpdl-wasm', license: 'AGPL-3.0', usage: 'Compress / PDF/A' },
  { name: 'MuPDF (mupdf.js)', url: 'https://github.com/ArtifexSoftware/mupdf.js', license: 'AGPL-3.0', usage: 'Redact' },
  { name: 'LibreOffice (WebAssembly)', url: 'https://github.com/matbeedotcom/libreoffice-document-converter', license: 'MPL-2.0', usage: 'Office → PDF' },
  { name: 'fflate', url: 'https://github.com/101arrowz/fflate', license: 'MIT', usage: 'ZIP / DOCX' },
  { name: 'Lucide icons', url: 'https://lucide.dev', license: 'ISC', usage: 'UI' },
  { name: 'Source Sans 3', url: 'https://github.com/adobe-fonts/source-sans', license: 'OFL-1.1', usage: 'UI' },
  { name: 'Cormorant Garamond', url: 'https://github.com/CatharsisFonts/Cormorant', license: 'OFL-1.1', usage: 'UI' },
  { name: 'Dancing Script', url: 'https://github.com/googlefonts/DancingScript', license: 'OFL-1.1', usage: 'Signature' },
  { name: 'Foxit / Liberation fonts (PDF.js)', url: 'https://github.com/mozilla/pdf.js/tree/master/external/standard_fonts', license: 'BSD / OFL-1.1', usage: 'Render' },
  { name: 'OpenJPEG, JBIG2, QCMS (PDF.js)', url: 'https://github.com/mozilla/pdf.js', license: 'BSD-2 / Apache-2.0 / MIT', usage: 'Render' },
];

export function licensesPage(): HTMLElement {
  const d = t();
  const p = d.pages.licenses;
  return h(
    'div',
    { class: 'container section' },
    h(
      'div',
      { class: 'prose' },
      h('h1', null, p.title),
      h('h2', null, p.appTitle),
      h('p', null, p.appText),
      h('p', null, h('a', { class: 'btn btn-outline', href: SOURCE_URL, target: '_blank', rel: 'noopener' }, icon('external-link'), p.sourceLink)),
      h('p', null, p.intro),
      h(
        'div',
        { class: 'table-wrap' },
        h(
          'table',
          { class: 'license-table' },
          h('thead', null, h('tr', null, h('th', null, p.component), h('th', null, p.license), h('th', null, p.usage))),
          h('tbody', null, LICENSES.map((l) => h('tr', null, h('td', null, h('a', { href: l.url, target: '_blank', rel: 'noopener' }, l.name)), h('td', null, l.license), h('td', null, l.usage)))),
        ),
      ),
    ),
  );
}

export function notFoundPage(lang: Lang): HTMLElement {
  const d = t();
  return h(
    'div',
    { class: 'container section' },
    h('h1', null, d.pages.notFound.title),
    h('p', null, d.pages.notFound.text),
    h('a', { class: 'btn btn-primary', href: homePath(lang) }, d.pages.notFound.back),
  );
}
